import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { missingPersonData } from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import {
  Clock, MapPin, User, CheckCircle, Search, Phone, Shield,
  Radio, Camera, Megaphone, Heart, ChevronDown, ChevronUp,
  Mic, AlertTriangle, Star, Users, Zap
} from "lucide-react";

// ── inline data ──────────────────────────────────────────────────
const CASE_DETAIL = {
  "MP-1185": {
    desc:"Child ~7yrs, Boy, yellow kurta, blue pyjama. Last seen Panchvati Sector 7 near Gate 5 at 14:10. Parents Mr. & Mrs. Shinde at Camp Beta.",
    physDesc:"Height: ~115cm. Short hair. Yellow kurta. Blue pyjama. Wearing rubber slippers.",
    actions:["PA announcement activated at all 6 zones at 14:18","WhatsApp broadcast sent to 22,000 pilgrims","CCTV scan in progress — Panchvati Gate 5 area","AI face match running (confidence 61%) — awaiting manual confirm","Parents contacted — at Camp Beta, Gate 6"],
    broadcast:"22,000 pilgrims reached via WhatsApp/SMS broadcast",
    aiMatch:"CCTV Gate 5 — 61% confidence — manual verification pending",
  },
  "MP-1184": {
    desc:"Elderly Male ~74yrs. Wearing white kurta-pyjama. Last seen Ramkund Entry 2 at 14:02. Memory issues — carries a brown bag.",
    physDesc:"Height: ~165cm. Grey hair. Dhoti kurta white. Brown bag. Walks with slight limp.",
    actions:["Located at Gate 4 medical rest area at 14:18","Inspector Shinde escorting to family at Camp Alpha","Family notified — eta 10 min for reunification"],
    broadcast:"8,400 pilgrims reached via SMS",
    aiMatch:"CCTV Gate 4 — 94% confidence — confirmed by officer",
  },
  "MP-1183": {
    desc:"Disabled Female ~52yrs. Wheelchair user. Last seen Kalaram Gate 3. Family member Sunil Lokhande searching.",
    physDesc:"Height: ~155cm. Saree — blue/green. Requires wheelchair. May carry bag with medical equipment.",
    actions:["UNIT-P14 coordinating search near Kalaram Gate 3","Wheelchair assistance team dispatched","PA announcement at Kalaram zone"],
    broadcast:"4,200 pilgrims reached via WhatsApp",
    aiMatch:"CCTV Kalaram Gate 3 — 42% confidence — below verification threshold, search ongoing",
  },
};

// Kept in sync with missingPersonData.cases: only cases actually marked
// "Resolved" there appear here (MP-1179 and MP-1178 are still "Active" in
// the live case list, so they don't belong in a reunification log). MP-1177
// predates today's tracked case window — a prior reunification, not a
// contradiction of any case currently open.
const REUNION_LOG = [
  { id:"MP-1182", name:"Male ~48yrs",       resolvedAt:"13:22", zone:"Trimbakeshwar Zone A", method:"PA Announcement",  officer:"UNIT-P12"    },
  { id:"MP-1181", name:"Child ~5yrs, Girl", resolvedAt:"13:05", zone:"Nashik Road Camp",     method:"Volunteer search", officer:"UNIT-P22"      },
  { id:"MP-1180", name:"Elderly Female",    resolvedAt:"12:48", zone:"Panchvati South",      method:"AI CCTV match",    officer:"UNIT-P16"    },
  { id:"MP-1177", name:"Elderly Male ~71",  resolvedAt:"11:58", zone:"Ramkund Gate 3",       method:"Help Desk",        officer:"UNIT-P11"    },
];

const HOURLY_REPORTS = [
  { hour:"06:00", reported:1, located:1, resolved:1 },
  { hour:"07:00", reported:2, located:2, resolved:1 },
  { hour:"08:00", reported:3, located:2, resolved:2 },
  { hour:"09:00", reported:4, located:3, resolved:3 },
  { hour:"10:00", reported:5, located:4, resolved:4 },
  { hour:"11:00", reported:3, located:3, resolved:3 },
  { hour:"12:00", reported:4, located:3, resolved:3 },
  { hour:"13:00", reported:2, located:2, resolved:2 },
  { hour:"14:00", reported:3, located:1, resolved:0 },
];

const RESOLUTION_TREND = [
  { day:"Mon", reported:4, resolved:3 },
  { day:"Tue", reported:6, resolved:5 },
  { day:"Wed", reported:8, resolved:6 },
  { day:"Thu", reported:5, resolved:5 },
  { day:"Fri", reported:7, resolved:4 },
  { day:"Sat", reported:11,resolved:6 },
];

const VOLUNTEER_TEAMS = [
  { zone:"Panchvati",       volunteers:18, status:"Active",   cases:3, lead:"Team Leader Suresh",  coverage:"Sectors 5–9"  },
  { zone:"Ramkund Ghat",    volunteers:12, status:"Active",   cases:2, lead:"Team Leader Priya",   coverage:"Gates 1–6"    },
  { zone:"Kalaram Temple",  volunteers:8,  status:"Searching",cases:1, lead:"Team Leader Ajay",    coverage:"Temple North"  },
  { zone:"Trimbakeshwar",   volunteers:10, status:"Active",   cases:2, lead:"Team Leader Rekha",   coverage:"All zones"    },
  { zone:"Nashik Road Stn", volunteers:6,  status:"Standby",  cases:1, lead:"Team Leader Manoj",   coverage:"Station area" },
  { zone:"Gangapur Camp",   volunteers:4,  status:"Standby",  cases:0, lead:"Team Leader Sapna",   coverage:"Camp zones"   },
];

const TECH_COVERAGE = [
  { tech:"CCTV Cameras",       total:842, active:826, coverage:98, note:"16 cameras offline — Ramkund Gate 3 area" },
  { tech:"Face Match AI",      total:1,   active:1,   coverage:88, note:"Processing 826 active feeds · 88.4% accuracy" },
  { tech:"PA System",          total:216, active:204, coverage:94, note:"12 speakers offline — Panchvati West" },
  { tech:"WhatsApp Broadcast", total:1,   active:1,   coverage:100,note:"Integrated with 22K+ pilgrim numbers" },
  { tech:"Radio Mesh Network", total:48,  active:48,  coverage:100,note:"All 6 zones covered — TETRA encrypted" },
];

// Children/Elderly/Disabled/Medical Risk counts read from missingPersonData
// directly (not re-typed) so this table can't drift from the case list.
const VULNERABILITY_PROFILE = [
  { type:"Children (0–14 yrs)",      count:missingPersonData.children, risk:"Highest", support:"Dedicated child protection desk · Consent guardian required" },
  { type:"Elderly (60+ yrs)",        count:missingPersonData.elderly,  risk:"High",    support:"Wheelchair service · medical escort · vernacular support"    },
  { type:"Disabled Pilgrims",        count:missingPersonData.disabled, risk:"High",    support:"Mobility aid · Camp Beta accessible zone · buddy system"    },
  { type:"Medical Risk (pre-exist.)",count:missingPersonData.medRisk,  risk:"Critical",support:"Priority triage · ambulance pre-assignment"                 },
  { type:"Language Barrier",         count:4,  risk:"Medium",  support:"Marathi/Hindi/Gujarati/Tamil volunteer translators"         },
  { type:"Cognitive / Memory",       count:2,  risk:"High",    support:"Wristband ID system · regular check-in at Zone Help Desks"  },
];

const BROADCAST_PERFORMANCE = [
  { id:"BC-341", case:"MP-1185 — Boy 7yr",     channel:"WhatsApp+SMS", reach:22000, seen:17800, leads:3, time:"14:18" },
  { id:"BC-340", case:"MP-1183 — Disabled F",  channel:"WhatsApp",     reach:4200,  seen:3600,  leads:1, time:"13:52" },
  { id:"BC-339", case:"MP-1185 — PA Zones",    channel:"PA System",    reach:84000, seen:84000, leads:5, time:"14:18" },
  { id:"BC-338", case:"MP-1184 — Elderly M",   channel:"SMS",          reach:8400,  seen:6800,  leads:2, time:"14:05" },
];

const AI_QUEUE = [
  { caseId:"MP-1185", model:"Face Match CNN",  confidence:61, frame:"CCTV-Gate5-14:28:14", status:"Pending Verify", officer:"UNIT-P16" },
  { caseId:"MP-1183", model:"Face Match CNN",  confidence:42, frame:"CCTV-Kal3-13:55:02",  status:"Low confidence", officer:"UNIT-P14" },
];

const STATUS_BADGE = s =>
  s === "Active"   ? "badge badge-critical" :
  s === "Located"  ? "badge badge-warning"  :
  s === "Resolved" ? "badge badge-success"  : "badge badge-info";

// Person category -> shared categorical palette, by fixed declaration order
// (stable index, not the person's status/severity).
const CAT_ORDER = ["Child", "Elderly", "Disabled", "Adult"];
const catColor = cat => {
  const idx = CAT_ORDER.indexOf(cat);
  return idx >= 0 ? chartColor(idx) : "#64748B";
};

const PIE_DATA = [
  { name:"Children", value: missingPersonData.children },
  { name:"Elderly",  value: missingPersonData.elderly  },
  { name:"Disabled", value: missingPersonData.disabled },
  { name:"Med. Risk",value: missingPersonData.medRisk  },
];

// Coverage % -> shared STATUS bucket (higher is better) for Technology
// Coverage bars/text, and AI queue confidence -> bucket.
const COVERAGE_BANDS = [{ min:95, bucket:"good" }, { min:80, bucket:"warning" }, { min:0, bucket:"critical" }];
const CONFIDENCE_BANDS = [{ min:70, bucket:"warning" }, { min:0, bucket:"critical" }];

// ── component ────────────────────────────────────────────────────
export default function MissingPersons() {
  const t = useTranslate();
  const [filter, setFilter]   = useState("All");
  const [expanded, setExpanded] = useState(null);

  const filtered = filter === "All"
    ? missingPersonData.cases
    : missingPersonData.cases.filter(c => c.status === filter);

  // Computed directly from the case list, not re-derived arithmetically from
  // the summary object — so this always agrees with the table underneath it.
  const activeCount   = missingPersonData.cases.filter(c => c.status === "Active").length;
  const locatedCount  = missingPersonData.cases.filter(c => c.status === "Located").length;
  const resolvedCount = missingPersonData.cases.filter(c => c.status === "Resolved").length;

  return (
    <div className="space-y-8">
      <SectionHeader icon="UserSearch" title="Missing Person & Vulnerable Pilgrim Support"
        subtitle="360° case management · AI face match · CCTV coverage · broadcast reach · reunification · child protection · elderly & disability support · volunteer coordination · DPDP 2023 compliance"
        badge={`${activeCount} ACTIVE`}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Total Cases"     value={missingPersonData.total.toString()}    icon="UserSearch" color="red"    index={0} live />
        <MetricCard title="Active"          value={activeCount.toString()}                icon="AlertTriangle" color="red" index={1} />
        <MetricCard title="Located"         value={locatedCount.toString()}               icon="MapPin"     color="amber"  index={2} />
        <MetricCard title="Resolved Today"  value={resolvedCount.toString()}               icon="CheckCircle"color="green"  index={3} />
        <MetricCard title="Children"        value={missingPersonData.children.toString()} icon="Users"      color="red"    index={4} />
        <MetricCard title="Elderly"         value={missingPersonData.elderly.toString()}  icon="Heart"      color="amber"  index={5} />
        <MetricCard title="Broadcast Reach" value="38.6K"                                 icon="Radio"      color="blue"   index={6} />
        <MetricCard title="Avg Resolve"     value="38 min"                                icon="Clock"      color="green"  index={7} />
      </div>

      {/* Command Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-red-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("MP COMMAND DESK — 14:33 IST —")} {activeCount} {t("CASES ACTIVE")}</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Missing Person & Vulnerable Pilgrim Command — Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {missingPersonData.total} {t("total cases today ·")} {resolvedCount} {t("resolved ·")} {locatedCount} {t("located ·")} {activeCount} {t("active ·")} {t("826 CCTV feeds live · AI face match at 88.4% · Broadcast reached 38.6K pilgrims · 58 volunteer search members deployed")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label:"CCTV Active", val:"826"              },
              { label:"Volunteers",  val:"58"               },
              { label:"Helpline",    val:"1800-266-0400"    },
              { label:"Avg Resolve", val:"38 min"           },
            ].map((b, i) => {
              const c = googleColor(i);
              return (
                <div key={b.label} className="rounded-xl px-3 py-2 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-base font-black leading-tight" style={{ color:c.text }}>{t(b.val)}</div>
                  <div className="text-[9px] mt-0.5" style={{ color:c.text }}>{t(b.label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* DPDP Callout */}
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
        className="rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 p-4">
        <div className="flex items-start gap-3">
          <Shield size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-black text-slate-900">{t("Consent-Based Vulnerable Pilgrim Support — DPDP 2023")}</h4>
            <p className="text-xs text-slate-600 mt-0.5 leading-relaxed max-w-2xl">
              {t("All biometric matching and face recognition uses")} <strong className="text-slate-800">{t("explicit consent-based workflows")}</strong>{t(".")}
              {" "}{t("AI face match results are advisory only — human officer must confirm before any action.")}
              {" "}{t("Data used only for reunification and deleted within 72 hours of event close.")}
              {" "}{t("No punitive action taken from AI facial recognition alone.")}
              {" "}{t("Child cases require guardian consent at every step.")}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {["Consent Gateway","Human-in-Loop","72hr Data Deletion","No Punitive AI","Guardian Auth for Minors"].map(tag => (
                <span key={tag} className="badge badge-success text-[9px]">{t(tag)}</span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Active Cases + Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ChartCard title="Category Distribution">
          <div className="flex items-center gap-3">
            <ResponsiveContainer width="55%" height={180}>
              <PieChart>
                <Pie data={PIE_DATA} dataKey="value" cx="50%" cy="50%"
                  outerRadius={72} innerRadius={42}
                  label={({ value }) => value} labelLine={false}>
                  {PIE_DATA.map((e, i) => <Cell key={i} fill={chartColor(i)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {PIE_DATA.map((d, i) => (
                <div key={d.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                    <span className="text-xs font-semibold text-slate-700">{t(d.name)}</span>
                  </div>
                  <span className="text-base font-black" style={{ color:chartColor(i) }}>{d.value}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-100 flex justify-between text-xs">
                <span className="text-slate-500">{t("Resolved")}</span>
                <span className="font-black text-emerald-600">{missingPersonData.resolved}</span>
              </div>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Hourly Case Activity" subtitle="Reported vs located vs resolved by hour">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={HOURLY_REPORTS} margin={{ top:4, right:5, left:-20, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="hour" tick={{ ...axisTick, fontSize:8 }} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="reported" fill={chartColor(0)} radius={[3,3,0,0]} name={t("Reported")} />
              <Bar dataKey="located"  fill={chartColor(1)} radius={[3,3,0,0]} name={t("Located")}  />
              <Bar dataKey="resolved" fill={chartColor(2)} radius={[3,3,0,0]} name={t("Resolved")} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Weekly Trend" subtitle="Reported vs resolved — 6 days">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={RESOLUTION_TREND} margin={{ top:4, right:5, left:-20, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="day" tick={axisTick} tickFormatter={d => t(d)} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="reported" fill={chartColor(0)} radius={[3,3,0,0]} name={t("Reported")} />
              <Bar dataKey="resolved" fill={chartColor(2)} radius={[3,3,0,0]} name={t("Resolved")} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Active Case Cards */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Active Case Detail — Click to expand")}</h3>
        <div className="space-y-2">
          {missingPersonData.cases.filter(c => c.status === "Active" || c.status === "Located").map((c, i) => {
            const isOpen = expanded === c.id;
            const detail = CASE_DETAIL[c.id];
            return (
              <motion.div key={c.id}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                className={`rounded-2xl border transition-all cursor-pointer ${
                  c.status === "Active" ? "border-red-200 bg-red-50/20" : "border-amber-200 bg-amber-50/20"
                } ${isOpen ? "shadow-card-hover" : ""}`}
                onClick={() => setExpanded(isOpen ? null : c.id)}
              >
                <div className="p-4 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-black font-mono text-blue-700 text-xs">{c.id}</span>
                      <span className={STATUS_BADGE(c.status)}>{t(c.status)}</span>
                      <span className="badge badge-navy text-[9px]">{t(c.category)}</span>
                    </div>
                    <p className="text-sm font-black text-slate-900 mb-0.5">{t(c.name)}</p>
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1"><MapPin size={9} />{t(c.zone)}</span>
                      <span className="flex items-center gap-1"><Clock size={9} />{t("Reported:")} {c.reportedAt}</span>
                      <span className="flex items-center gap-1"><User size={9} />{c.assignee}</span>
                    </div>
                  </div>
                  {c.status === "Active" && <div className="w-2.5 h-2.5 rounded-full bg-red-500 live-dot flex-shrink-0" />}
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" /> :
                             <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
                </div>
                <AnimatePresence>
                  {isOpen && detail && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-white/60">
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white/60 rounded-b-2xl">
                        <div className="space-y-3">
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Situation")}</div>
                            <p className="text-xs text-slate-700 leading-relaxed">{t(detail.desc)}</p>
                          </div>
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Physical Description")}</div>
                            <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded-lg">{t(detail.physDesc)}</p>
                          </div>
                          <div className="p-2.5 rounded-xl bg-violet-50 border border-violet-200">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Camera size={10} className="text-violet-600" />
                              <span className="text-[9px] font-black text-violet-700">{t("AI Face Match Status")}</span>
                            </div>
                            <p className="text-[10px] text-violet-800">{t(detail.aiMatch)}</p>
                            <p className="text-[9px] text-violet-500 mt-1">⚠ {t("Human officer confirmation required before any action — DPDP 2023")}</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Actions Taken")}</div>
                            <div className="space-y-1.5">
                              {detail.actions.map((a, idx) => (
                                <div key={idx} className="flex items-start gap-1.5">
                                  <CheckCircle size={10} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                                  <span className="text-[10px] text-slate-700 leading-snug">{t(a)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="p-2.5 rounded-xl bg-blue-50 border border-blue-200">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Megaphone size={10} className="text-blue-600" />
                              <span className="text-[9px] font-black text-blue-700">{t("Broadcast Performance")}</span>
                            </div>
                            <p className="text-[10px] text-blue-800">{t(detail.broadcast)}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Full Case Table */}
      <ChartCard title="All Cases — Case Management Table"
        subtitle="Complete register of missing person and vulnerable pilgrim cases today"
        action={
          <div className="flex gap-1">
            {["All","Active","Located","Resolved"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`text-[9px] font-black px-2 py-1 rounded-lg transition-colors ${
                  filter === f ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(f)}</button>
            ))}
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th>{t("Case ID")}</th><th>{t("Name / Description")}</th><th>{t("Category")}</th>
                <th>{t("Reported")}</th><th>{t("Zone")}</th><th>{t("Assigned")}</th><th>{t("Contact")}</th><th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <motion.tr key={c.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td className="font-black text-blue-700 font-mono">{c.id}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: catColor(c.category) }} />
                      <span className="font-semibold text-slate-800">{t(c.name)}</span>
                    </div>
                  </td>
                  <td><span className="badge badge-navy text-[9px]">{t(c.category)}</span></td>
                  <td className="text-slate-500"><Clock size={9} className="inline mr-1" />{c.reportedAt}</td>
                  <td className="text-slate-600">
                    <MapPin size={9} className="inline mr-1" />
                    <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline underline-offset-2">{t(c.zone)}</Link>
                  </td>
                  <td><User size={9} className="inline mr-1 text-blue-600" />{c.assignee}</td>
                  <td className="text-blue-700 font-mono text-[10px]">{c.phone}</td>
                  <td><span className={STATUS_BADGE(c.status)}>{t(c.status)}</span></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div><div className="text-xl font-black text-red-600">{missingPersonData.cases.filter(c=>c.status==="Active").length}</div><div className="text-[10px] text-slate-500">{t("Active")}</div></div>
          <div><div className="text-xl font-black text-amber-600">{missingPersonData.cases.filter(c=>c.status==="Located").length}</div><div className="text-[10px] text-slate-500">{t("Located")}</div></div>
          <div><div className="text-xl font-black text-emerald-600">{missingPersonData.cases.filter(c=>c.status==="Resolved").length}</div><div className="text-[10px] text-slate-500">{t("Resolved")}</div></div>
          <div><div className="text-xl font-black text-blue-700">{missingPersonData.total}</div><div className="text-[10px] text-slate-500">{t("Total")}</div></div>
        </div>
      </ChartCard>

      {/* Reunion Log + Broadcast Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Reunification Log — Resolved Today"
          subtitle="Successfully reunited pilgrims with families — method and officer">
          <div className="space-y-2">
            {REUNION_LOG.map((r, i) => (
              <motion.div key={r.id}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className="flex items-center gap-3 p-2.5 rounded-xl border border-emerald-200 bg-emerald-50/20"
              >
                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={12} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-[9px] text-slate-400">{r.id}</span>
                    <span className="text-xs font-bold text-slate-800 truncate">{t(r.name)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[9px] text-slate-500 flex-wrap mt-0.5">
                    <span><MapPin size={8} className="inline" /> {t(r.zone)}</span>
                    <span className="badge badge-success text-[8px]">{t(r.method)}</span>
                  </div>
                </div>
                <div className="text-right text-[9px] flex-shrink-0">
                  <div className="font-bold text-slate-700">{r.officer}</div>
                  <div className="text-slate-400">{r.resolvedAt}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Broadcast Performance"
          subtitle="Reach and response from missing person alert broadcasts">
          <div className="space-y-3">
            {BROADCAST_PERFORMANCE.map((b, i) => (
              <motion.div key={b.id}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className="p-3 rounded-xl border border-blue-200 bg-blue-50/20"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <span className="font-mono text-[9px] text-slate-400">{b.id}</span>
                    <div className="text-xs font-bold text-slate-800">{t(b.case)}</div>
                    <div className="text-[9px] text-slate-500">{t(b.channel)} · {b.time}</div>
                  </div>
                  <div className="text-right text-[9px]">
                    <div className="font-black text-slate-900">{b.reach.toLocaleString()}</div>
                    <div className="text-slate-400">{t("reach")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-1.5 rounded-full bg-blue-500" style={{ width:`${Math.round(b.seen/b.reach*100)}%` }} />
                  </div>
                  <span className="text-[9px] text-slate-500 flex-shrink-0">{Math.round(b.seen/b.reach*100)}% {t("seen")}</span>
                  <span className="text-[9px] font-black text-emerald-600 flex-shrink-0">{b.leads} {t("leads")}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Volunteer + Technology */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Volunteer Search Teams — Zone Deployment"
          subtitle="Active search teams with coverage areas and open case counts">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {VOLUNTEER_TEAMS.map((z, i) => (
              <motion.div key={z.zone}
                initial={{ opacity:0, scale:0.96 }} animate={{ opacity:1, scale:1 }} transition={{ delay: i * 0.07 }}
                className={`card p-3 border ${z.status === "Active" || z.status === "Searching" ? "border-blue-200 bg-blue-50/20" : "border-slate-200"}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <Link to="/field-ops" className="text-xs font-black text-slate-800 hover:text-blue-700 hover:underline underline-offset-2">{t(z.zone)}</Link>
                  <span className={`badge text-[8px] ${z.status === "Active" ? "badge-live" : z.status === "Searching" ? "badge-warning" : "badge-info"}`}>{t(z.status)}</span>
                </div>
                <div className="text-[9px] text-slate-500 mb-1.5">{z.lead}</div>
                <div className="text-[9px] text-slate-500 mb-1.5">{t(z.coverage)}</div>
                <div className="flex items-center gap-3 text-[10px]">
                  <span><strong className="text-blue-700">{z.volunteers}</strong> <span className="text-slate-500">{t("volunteers")}</span></span>
                  <span><strong className={z.cases > 0 ? "text-red-600" : "text-emerald-600"}>{z.cases}</strong> <span className="text-slate-500">{t("open")}</span></span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Technology Coverage — Search Infrastructure"
          subtitle="CCTV · AI face match · PA system · WhatsApp · Radio mesh">
          <div className="space-y-3">
            {TECH_COVERAGE.map((tc, i) => (
              <motion.div key={tc.tech}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="text-xs font-semibold text-slate-700">{t(tc.tech)}</span>
                  <div className="flex items-center gap-1.5 text-[9px] flex-shrink-0">
                    <span className="text-slate-500">{tc.active}/{tc.total}</span>
                    <span className={`font-black ${STATUS[thresholdStatus(tc.coverage, COVERAGE_BANDS)].text}`}>{tc.coverage}%</span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1">
                  <motion.div className={`h-2 rounded-full ${STATUS[thresholdStatus(tc.coverage, COVERAGE_BANDS)].bar}`}
                    initial={{ width:0 }} animate={{ width:`${tc.coverage}%` }} transition={{ duration:0.8, delay: i * 0.07 }} />
                </div>
                <div className="text-[9px] text-slate-400">{t(tc.note)}</div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100">
            {AI_QUEUE.length > 0 && (
              <>
                <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("AI Face Match Queue")}</div>
                {AI_QUEUE.map((q, i) => (
                  <div key={q.caseId} className="p-2.5 rounded-xl border border-violet-200 bg-violet-50/20 mb-1.5">
                    <div className="flex items-center justify-between text-[9px]">
                      <div>
                        <span className="font-black text-violet-700">{q.caseId}</span>
                        <span className="text-slate-500 ml-1.5">{t(q.model)}</span>
                      </div>
                      <span className={`font-black ${STATUS[thresholdStatus(q.confidence, CONFIDENCE_BANDS)].text}`}>{q.confidence}% {t("conf.")}</span>
                    </div>
                    <div className="text-[9px] text-slate-500 mt-0.5">{q.frame} · {t(q.status)}</div>
                    <div className="text-[9px] text-blue-700 font-bold mt-0.5">{t("Assigned:")} {q.officer} — {t("approval required")}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        </ChartCard>
      </div>

      {/* Vulnerability Profile */}
      <ChartCard title="Vulnerable Pilgrim Support Matrix"
        subtitle="Category-wise vulnerability profile and dedicated support protocols — Simhastha 2027">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {VULNERABILITY_PROFILE.map((v, i) => {
            const riskBucket =
              v.risk === "Highest"  ? "critical" :
              v.risk === "Critical" ? "critical" :
              v.risk === "High"     ? "serious"  :
              v.risk === "Medium"   ? "warning"  : "info";
            return (
            <motion.div key={v.type}
              initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
              className="p-3 rounded-xl border border-slate-200 hover:shadow-card-hover transition-shadow"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1">
                  <div className="text-xs font-black text-slate-900">{t(v.type)}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${STATUS[riskBucket].bg} ${STATUS[riskBucket].text}`}>{t(v.risk)} {t("Risk")}</span>
                  </div>
                </div>
                <div className="text-2xl font-black flex-shrink-0" style={{ color:chartColor(i) }}>{v.count}</div>
              </div>
              <p className="text-[9px] text-slate-600 leading-snug">{t(v.support)}</p>
            </motion.div>
            );
          })}
        </div>
      </ChartCard>
    </div>
  );
}





